// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/runtimePreset.js
//
// A schema-v2 record plus its decoded canvases becomes the runtime preset
// the canvas uses. The runtime object keeps the flat fields the existing
// renderer, cursor, 3D painting and collaboration preview read, and adds
// the v2 fields (brushSettings, brushDynamics, brushTexture) that the
// planner and GPU stroke session use.
import { createImportedBrushBlobPreviewFromImageData } from '../importedBrushUtils';
import { isPaintableRecord } from './brushSchema.js';

function blendCurve(response) {
  if (!response || !(response.amount > 0)) return [];
  const curve = Array.isArray(response.curve) && response.curve.length
    ? response.curve
    : [{ x: 0, y: 0 }, { x: 1, y: 1 }];
  return curve.map((point) => ({ x: point.x, y: 1 - response.amount * (1 - point.y) }));
}

function legacyRotationMode(rotation) {
  if (!rotation) return 'none';
  if (rotation.jitter >= Math.PI) return 'random';
  if (rotation.azimuth) return 'tilt';
  if (Math.abs(rotation.follow) >= 0.5) return 'stroke';
  return 'none';
}

function blobPreviewFromCanvas(canvas) {
  if (!canvas || !canvas.width || !canvas.height) return null;
  try {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    return createImportedBrushBlobPreviewFromImageData(ctx.getImageData(0, 0, canvas.width, canvas.height), { maskMode: 'alpha' });
  } catch {
    return null;
  }
}

// assets: { tips: [canvas], grain: canvas|null, previewUrl, thumbnailUrl }
export function buildRuntimePreset(record, assets = {}) {
  const tips = (assets.tips || []).filter(Boolean);
  const settings = record.settings;
  const dynamics = record.dynamics;
  const loaded = tips.length > 0;
  return {
    id: record.id,
    name: record.name,
    localOnly: true,
    category: record.library.category,
    folder: record.library.folder,
    favourite: record.library.favourite,
    sourcePath: record.source.fileName,
    sourceApp: record.source.app,
    sourceEngine: record.source.engine,
    compatibility: record.compatibility,
    paintable: isPaintableRecord(record),
    loaded,
    brushRecord: record,
    previewUrl: assets.previewUrl || '',
    thumbnailDataUrl: assets.previewUrl || assets.thumbnailUrl || '',
    originalTipDataUrl: '',
    originalTipCanvas: tips[0] || null,
    originalTipCanvases: tips,
    originalGrainCanvas: record.texture ? assets.grain || null : null,
    blobPreview: loaded ? blobPreviewFromCanvas(tips[0]) : null,
    tipSelection: record.assets.tipSelection,
    tipColour: settings.tipColour,
    brushSettings: settings,
    brushDynamics: dynamics,
    brushTexture: record.texture && assets.grain ? record.texture : null,
    brushRendering: record.rendering,
    // Flat fields kept for the 2D/3D renderer and older code paths.
    spacing: settings.spacing,
    scatter: dynamics.scatter.perpendicular,
    rotationMode: legacyRotationMode(dynamics.rotation),
    pressureToSize: dynamics.size.pressure.amount > 0,
    pressureToOpacity: dynamics.opacity.pressure.amount > 0,
    pressureToFlow: dynamics.flow.pressure.amount > 0,
    pressureToSpacing: dynamics.spacing.pressure.amount > 0,
    sizeCurve: blendCurve(dynamics.size.pressure),
    opacityCurve: blendCurve(dynamics.opacity.pressure),
    flowCurve: blendCurve(dynamics.flow.pressure),
    spacingCurve: blendCurve(dynamics.spacing.pressure),
    tipScale: settings.tipScale,
    fixedAngle: settings.angle,
    colorAsMask: settings.tipColour !== 'image',
    maskMode: 'alpha',
    edgeMode: settings.edgeMode,
    flow: settings.flow,
    brushOpacity: settings.opacity,
    procreateShapeCount: 1,
    procreateTextureDepth: 0,
  };
}
